import React from "react";
import { ChevronRight } from "lucide-react";
import { Avatar, StatusBadge, fmtDateShort, relTime, operatorsLabel } from "./clientsUi";

// Desktop operative list — compact rows, discreet coral signal for unread leads.
export function ClientsTable({ items, selectedId, onSelect }) {
  return (
    <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="clients-list-view">
      <table className="w-full text-[13px] table-fixed">
        <thead className="bg-neutral-50/70 text-[10px] uppercase tracking-[0.12em] text-neutral-500">
          <tr>
            <th className="text-left px-4 py-2.5 w-[30%]">Cliente</th>
            <th className="text-left px-3 py-2.5 w-[13%]">Matrimonio</th>
            <th className="text-left px-3 py-2.5 w-[15%]">Location</th>
            <th className="text-left px-3 py-2.5 w-[14%]">Operatore</th>
            <th className="text-left px-3 py-2.5 w-[16%]">Stato</th>
            <th className="text-right px-4 py-2.5 w-[12%]">Ultima attività</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((c) => {
            const active = c.id === selectedId;
            return (
              <tr
                key={c.id}
                onClick={() => onSelect(c)}
                className={`cursor-pointer transition-colors relative ${active ? "bg-primary/5" : "hover:bg-neutral-50"}`}
                data-testid={`list-row-${c.id}`}
                aria-selected={active}
              >
                <td className={`px-4 py-3 ${c.is_unread_lead ? "border-l-[3px] border-l-rose-500" : active ? "border-l-[3px] border-l-primary" : "border-l-[3px] border-l-transparent"}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={c.name} unread={c.is_unread_lead} size="sm" />
                    <div className="min-w-0">
                      <div className="font-medium text-neutral-900 truncate flex items-center gap-1.5">
                        {c.is_unread_lead && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" aria-hidden />}
                        <span className="truncate">{c.name || "—"}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">{c.partner_name ? `& ${c.partner_name}` : (c.email || "—")}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 tabular-nums text-neutral-800 truncate">{fmtDateShort(c.wedding_date)}</td>
                <td className="px-3 py-3 text-neutral-700 truncate">{c.venue || "—"}</td>
                <td className="px-3 py-3 text-neutral-700 truncate">{c.operators?.length ? operatorsLabel(c) : <span className="text-neutral-400">—</span>}</td>
                <td className="px-3 py-3"><StatusBadge c={c} /></td>
                <td className="px-4 py-3 text-right text-neutral-500 text-[12px] whitespace-nowrap">
                  {relTime(c.last_activity_at)} <ChevronRight className="w-3.5 h-3.5 inline text-neutral-300 ml-1" strokeWidth={1.6} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Mobile compact row — one tap opens the quick detail. No inline action bar.
export function ClientRowMobile({ c, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(c)}
      className={`w-full text-left bg-white border rounded-md px-3 py-3 flex items-start gap-3 active:bg-neutral-50 ${c.is_unread_lead ? "border-rose-100 shadow-[inset_3px_0_0_0_#f43f5e]" : "border-border"}`}
      data-testid={`mobile-row-${c.id}`}
    >
      <Avatar name={c.name} unread={c.is_unread_lead} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-serif-heading text-[17px] leading-tight truncate">{c.name || "—"}</span>
          <StatusBadge c={c} className="!h-5 !text-[10px]" />
        </div>
        <div className="mt-1 text-[12.5px] text-neutral-600 truncate">{fmtDateShort(c.wedding_date)}{c.venue ? ` · ${c.venue}` : ""}</div>
        <div className="text-[12px] text-neutral-500 truncate">Operatore: {operatorsLabel(c)}</div>
        <div className="text-[12px] text-neutral-500">Ultima attività: {relTime(c.last_activity_at)}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-neutral-300 mt-3 shrink-0" strokeWidth={1.6} />
    </button>
  );
}
